import { useUserIdentity } from '@src/stores-v2/StoreContext';
import { useVideoCreatorStore } from '@src/stores-v2/VideoCreatorStoreContext';
import { AIProducerCard } from '@src/types.ts/general';
import { useFlagsCombination } from '@src/utility/useFlagsCombination';

export interface AiProducerCardFlags {
  clipProducerOnly: boolean;
  photo: boolean;
  clip: boolean;
  scenes: boolean;
  framing: boolean;
}

/** The AI Producer cards the flags allow, in tab order. */
export function getVisibleAiProducerCards(
  flags: AiProducerCardFlags,
): AIProducerCard[] {
  if (flags.clipProducerOnly) return [AIProducerCard.clip];
  return [
    flags.photo && AIProducerCard.photo,
    AIProducerCard.music,
    flags.clip && AIProducerCard.clip,
    flags.scenes && AIProducerCard.scenes,
    flags.framing && AIProducerCard.framing,
  ].filter((card): card is AIProducerCard => !!card);
}

/** The requested card if visible, otherwise the first visible one. */
export function resolveAiProducerCard(
  requested: AIProducerCard,
  visible: AIProducerCard[],
): AIProducerCard {
  return visible.includes(requested) ? requested : visible[0];
}

/**
 * `aiProducerSubMenu` is the requested card, and several paths write cards the
 * flags hide. Tabs and panel both read `activeCard` so they always agree.
 * Call from an `observer` component.
 */
export function useAiProducerCards() {
  const videoCreator = useVideoCreatorStore();
  const userIdentity = useUserIdentity();
  const {
    creatorStudioAiProducerEnableClipProducerOnly,
    creatorStudioAiProducerEnablePhotoTab,
    enableFramingTab,
    enableDetectScenesButton,
    enableAutoFraming,
  } = useFlagsCombination(userIdentity.currentRole);

  const cards = getVisibleAiProducerCards({
    clipProducerOnly: creatorStudioAiProducerEnableClipProducerOnly,
    photo: creatorStudioAiProducerEnablePhotoTab,
    clip: enableFramingTab,
    scenes: enableDetectScenesButton,
    framing: enableAutoFraming,
  });
  const activeCard = resolveAiProducerCard(
    videoCreator.aiProducerSubMenu,
    cards,
  );
  return { cards, activeCard };
}
